import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import apiClient from '../api/axios';
import { toast } from 'react-toastify';

const AssignRolesModal = ({ show, handleClose, userToEdit, onUpdate }) => {
    const [roles, setRoles] = useState({});
    const [selectedRoles, setSelectedRoles] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const response = await apiClient.get(`/event-roles/`);
                // Группируем роли по направлениям для удобного отображения
                const grouped = response.data.reduce((acc, role) => {
                    const departmentName = role.department.name || 'Без направления';
                    if (!acc[departmentName]) {
                        acc[departmentName] = [];
                    }
                    acc[departmentName].push(role);
                    return acc;
                }, {});
                setRoles(grouped);
            } catch (err) {
                toast.error('Не удалось загрузить список ролей.');
            }
        };

        if (show) {
            fetchRoles();
        }
    }, [show]);

    useEffect(() => {
        if (userToEdit) {
            const initialSelected = new Set(userToEdit.roles.map(r => r.id));
            setSelectedRoles(initialSelected);
        }
    }, [userToEdit]);

    const handleCheckboxChange = (roleId) => {
        const newSelection = new Set(selectedRoles);
        if (newSelection.has(roleId)) {
            newSelection.delete(roleId);
        } else {
            newSelection.add(roleId);
        }
        setSelectedRoles(newSelection);
    };

    const handleSubmit = async () => {
        if (!userToEdit) return;
        setLoading(true);
        setError(null);

        const initialRoles = new Set(userToEdit.roles.map(r => r.id));
        const finalRoles = selectedRoles;

        const toAdd = [...finalRoles].filter(id => !initialRoles.has(id));
        const toRemove = [...initialRoles].filter(id => !finalRoles.has(id));

        try {
            const promises = [];
            toAdd.forEach(roleId => {
                promises.push(apiClient.post(`/users/${userToEdit.id}/roles/${roleId}`));
            });
            toRemove.forEach(roleId => {
                promises.push(apiClient.delete(`/users/${userToEdit.id}/roles/${roleId}`));
            });

            await Promise.all(promises);

            toast.success('Роли пользователя успешно обновлены.');
            onUpdate(); // Refresh the user list
            handleClose();
        } catch (err) {
            const errorMessage = err.response?.data?.detail || 'Ошибка при обновлении ролей.';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Назначить роли для {userToEdit?.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                <p>Выберите роли, которые может выполнять пользователь.</p>
                <Form>
                    {Object.entries(roles).map(([departmentName, departmentRoles]) => (
                        <div key={departmentName} className="mb-3">
                            <strong>{departmentName}</strong>
                            {departmentRoles.map(role => (
                                <Form.Check 
                                    type="checkbox"
                                    key={role.id}
                                    id={`role-${role.id}`}
                                    label={role.name}
                                    checked={selectedRoles.has(role.id)}
                                    onChange={() => handleCheckboxChange(role.id)}
                                />
                            ))}
                        </div>
                    ))}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>Отмена</Button>
                <Button variant="primary" onClick={handleSubmit} disabled={loading}>
                    {loading ? 'Сохранение...' : 'Сохранить'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AssignRolesModal;
